import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Button,
  Heading,
  Input,
  LoadingSpinner,
  Text,
  Tooltip,
} from '../../components/ui'
import { usePermission } from '../../hooks/usePermissions'
import { useUserLogs } from '../../hooks/useUserLogs'
import VersionDiff from '../../components/VersionDiff'
import {
  createFlow,
  deleteFlow,
  getFlowVersions,
  listFlows,
  restoreFlowVersion,
  updateFlow,
} from '../../services/n8nFlows'
import type { N8nFlow, N8nFlowVersion } from '../../types/n8nFlows'
import type { WikiArticleVersion } from '../../types/wiki'
import {
  AdjustmentsHorizontalIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  ClockIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'

const MODULE_NAME = 'fluxo_n8n'
const LOG_ORIGEM = 'ia/fluxo-n8n'
const ITEMS_PER_PAGE = 25

function formatDateTime(value: string): string {
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value))
}

function truncate(text: string | null | undefined, max = 80): string {
  if (!text) return '—'
  const normalized = text.trim()
  if (!normalized) return '—'
  return normalized.length > max ? `${normalized.slice(0, max)}…` : normalized
}

function formatFlowJson(value: unknown): string {
  try {
    return JSON.stringify(value ?? {}, null, 2)
  } catch {
    return String(value ?? '')
  }
}

function isValidJson(text: string): boolean {
  const trimmed = text.trim()
  if (!trimmed) return false
  try {
    JSON.parse(trimmed)
    return true
  } catch {
    return false
  }
}

function sanitizeFileNameBase(title: string): string {
  const normalized = title
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
  return normalized || 'fluxo-n8n'
}

function downloadJsonFile(data: unknown, fileNameBase: string) {
  const json = typeof data === 'string' ? data : JSON.stringify(data ?? {}, null, 2)
  const blob = new Blob([json], { type: 'application/json;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  try {
    const a = document.createElement('a')
    a.href = url
    a.download = `${sanitizeFileNameBase(fileNameBase)}.json`
    a.rel = 'noopener'
    document.body.appendChild(a)
    a.click()
    a.remove()
  } finally {
    URL.revokeObjectURL(url)
  }
}

function toDiffVersions(versions: N8nFlowVersion[]): WikiArticleVersion[] {
  return versions.map(v => ({
    id: v.id,
    article_id: v.flow_id,
    version_number: v.version_number,
    title: v.title,
    body: formatFlowJson(v.flow_json),
    created_at: v.created_at,
    created_by: v.created_by ?? undefined,
    change_summary: v.change_summary ?? undefined,
    user_name: v.user_name,
  }))
}

function flowLogFields(row: {
  id?: string
  title: string
  description: string | null
  flow_json: unknown
  current_version?: number | null
}) {
  return {
    id: row.id ?? null,
    title: row.title,
    description: row.description,
    flow_json: row.flow_json,
    current_version: row.current_version ?? null,
  }
}

const emptyForm = { title: '', description: '', flow_json: '' }

const FluxoN8nListaTab: React.FC = () => {
  const { hasPermission: canInsert } = usePermission(MODULE_NAME, 'insert')
  const { hasPermission: canUpdate } = usePermission(MODULE_NAME, 'update')
  const { hasPermission: canDelete } = usePermission(MODULE_NAME, 'delete')
  const { logCreate, logUpdate, logDelete } = useUserLogs()

  const [items, setItems] = useState<N8nFlow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [appliedSearch, setAppliedSearch] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(0)
  const [totalItems, setTotalItems] = useState(0)

  const [showFormModal, setShowFormModal] = useState(false)
  const [editing, setEditing] = useState<N8nFlow | null>(null)
  const [formData, setFormData] = useState(emptyForm)
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState(false)
  const jsonFileInputRef = useRef<HTMLInputElement>(null)

  const [showViewModal, setShowViewModal] = useState(false)
  const [viewing, setViewing] = useState<N8nFlow | null>(null)

  const [showVersionsModal, setShowVersionsModal] = useState(false)
  const [versionsItem, setVersionsItem] = useState<N8nFlow | null>(null)
  const [versions, setVersions] = useState<N8nFlowVersion[]>([])
  const [versionsLoading, setVersionsLoading] = useState(false)
  const [showVersionViewModal, setShowVersionViewModal] = useState(false)
  const [viewingVersion, setViewingVersion] = useState<N8nFlowVersion | null>(null)
  const [showVersionDiffModal, setShowVersionDiffModal] = useState(false)

  const loadData = useCallback(async (page = currentPage, query = appliedSearch) => {
    setLoading(true)
    setError(null)
    try {
      const result = await listFlows(
        { query: query || undefined, sort_by: 'updated_at', sort_order: 'desc' },
        page,
        ITEMS_PER_PAGE
      )
      setItems(result.flows)
      setTotalPages(result.totalPages)
      setTotalItems(result.total)
      setCurrentPage(result.page)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar fluxos')
      setItems([])
    } finally {
      setLoading(false)
    }
  }, [appliedSearch, currentPage])

  useEffect(() => {
    void loadData()
  }, [loadData])

  const handleBuscar = () => {
    setAppliedSearch(searchTerm.trim())
    setCurrentPage(1)
  }

  const openCreate = () => {
    setEditing(null)
    setFormData(emptyForm)
    setFormErrors({})
    setShowFormModal(true)
  }

  const openEdit = (item: N8nFlow) => {
    setEditing(item)
    setFormData({
      title: item.title,
      description: item.description || '',
      flow_json: formatFlowJson(item.flow_json),
    })
    setFormErrors({})
    setShowFormModal(true)
  }

  const openView = (item: N8nFlow) => {
    setViewing(item)
    setShowViewModal(true)
  }

  const validateForm = () => {
    const errors: Record<string, string> = {}
    if (!formData.title.trim()) errors.title = 'Título é obrigatório'
    if (!formData.flow_json.trim()) {
      errors.flow_json = 'JSON é obrigatório'
    } else if (!isValidJson(formData.flow_json)) {
      errors.flow_json = 'JSON inválido'
    }
    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const canSaveForm = useMemo(
    () =>
      formData.title.trim().length > 0 &&
      formData.flow_json.trim().length > 0 &&
      isValidJson(formData.flow_json),
    [formData.title, formData.flow_json]
  )

  const handleJsonFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    try {
      const text = await file.text()
      const parsed = JSON.parse(text)
      const formatted = JSON.stringify(parsed, null, 2)
      setFormData(prev => ({ ...prev, flow_json: formatted }))
      setFormErrors(prev => {
        const next = { ...prev }
        delete next.flow_json
        return next
      })
    } catch {
      setFormErrors(prev => ({ ...prev, flow_json: 'Arquivo JSON inválido' }))
    } finally {
      e.target.value = ''
    }
  }

  const handleSave = async () => {
    if (!validateForm()) return
    if (editing && !canUpdate) return
    if (!editing && !canInsert) return

    setSaving(true)
    setError(null)
    try {
      if (editing) {
        const oldValues = flowLogFields(editing)
        await updateFlow({
          id: editing.id,
          title: formData.title,
          description: formData.description,
          flow_json: formData.flow_json,
        })
        const parsedJson = JSON.parse(formData.flow_json.trim())
        await logUpdate(
          'ia_n8n_flow',
          editing.id,
          `Atualizou fluxo N8N: ${formData.title.trim()}`,
          oldValues,
          flowLogFields({
            id: editing.id,
            title: formData.title.trim(),
            description: formData.description.trim() || null,
            flow_json: parsedJson,
          }),
          { origem: LOG_ORIGEM }
        )
      } else {
        const id = await createFlow(formData)
        const parsedJson = JSON.parse(formData.flow_json.trim())
        await logCreate(
          'ia_n8n_flow',
          id,
          `Criou fluxo N8N: ${formData.title.trim()}`,
          {
            origem: LOG_ORIGEM,
            new_values: flowLogFields({
              id,
              title: formData.title.trim(),
              description: formData.description.trim() || null,
              flow_json: parsedJson,
            }),
          }
        )
      }
      setShowFormModal(false)
      await loadData(editing ? currentPage : 1, appliedSearch)
      if (!editing) setCurrentPage(1)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar fluxo')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (item: N8nFlow) => {
    if (!canDelete) return
    if (!window.confirm(`Excluir o fluxo "${item.title}"? Esta ação não pode ser desfeita.`)) {
      return
    }
    try {
      await deleteFlow(item.id)
      await logDelete(
        'ia_n8n_flow',
        item.id,
        `Excluiu fluxo N8N: ${item.title}`,
        {
          origem: LOG_ORIGEM,
          old_values: flowLogFields(item),
        }
      )
      await loadData(currentPage, appliedSearch)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao excluir fluxo')
    }
  }

  const openVersionsModal = async (item: N8nFlow) => {
    setVersionsItem(item)
    setShowVersionsModal(true)
    setVersionsLoading(true)
    try {
      const data = await getFlowVersions(item.id)
      setVersions(data)
    } catch (err) {
      console.error('Erro ao carregar versões:', err)
      setVersions([])
    } finally {
      setVersionsLoading(false)
    }
  }

  const handleRestoreVersion = async (versionNumber: number) => {
    if (!canUpdate || !versionsItem) return
    if (!window.confirm(`Restaurar a versão ${versionNumber}? Uma nova versão será criada.`)) {
      return
    }
    try {
      const before = flowLogFields(versionsItem)
      await restoreFlowVersion(versionsItem.id, versionNumber)
      await logUpdate(
        'ia_n8n_flow',
        versionsItem.id,
        `Restaurou versão ${versionNumber} do fluxo N8N: ${versionsItem.title}`,
        before,
        { restored_from_version: versionNumber },
        { origem: LOG_ORIGEM }
      )
      setShowVersionsModal(false)
      setVersionsItem(null)
      setVersions([])
      await loadData(currentPage, appliedSearch)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao restaurar versão')
    }
  }

  return (
    <div className="flex-1 flex flex-col overflow-y-auto">
      <div className="px-6 py-4 space-y-4">
        {error && (
          <div className="rounded-lg border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-950/30 px-4 py-3">
            <Text size="sm" className="text-red-700 dark:text-red-300">
              {error}
            </Text>
          </div>
        )}

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                Fluxos
              </Heading>
              <Text size="sm" color="muted" className="mt-1">
                {totalItems} {totalItems === 1 ? 'registro' : 'registros'}
              </Text>
            </div>
            {canInsert && (
              <Button variant="solid" size="sm" onClick={openCreate} className="inline-flex items-center gap-2">
                <PlusIcon className="h-4 w-4" />
                Novo fluxo
              </Button>
            )}
          </div>

          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="flex-1 max-w-md">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Buscar
              </label>
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') handleBuscar()
                  }}
                  placeholder="Título ou descrição..."
                  className="pl-9"
                />
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={handleBuscar}>
              Buscar
            </Button>
          </div>

          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <LoadingSpinner />
              </div>
            ) : items.length === 0 ? (
              <div className="py-16 text-center">
                <Text color="muted">Nenhum fluxo encontrado.</Text>
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Título
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Descrição
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Versão
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Atualizado em
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {items.map(item => (
                    <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">
                        {item.title}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300 max-w-xs">
                        {truncate(item.description)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300 tabular-nums">
                        v{item.current_version || 0}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                        {formatDateTime(item.updated_at)}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="inline-flex items-center gap-2">
                          <Tooltip content="Visualizar">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openView(item)}
                              aria-label={`Visualizar ${item.title}`}
                            >
                              <EyeIcon className="h-4 w-4" />
                            </Button>
                          </Tooltip>
                          <Tooltip content="Histórico de versões">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => void openVersionsModal(item)}
                              aria-label={`Versões de ${item.title}`}
                            >
                              <ClockIcon className="h-4 w-4" />
                            </Button>
                          </Tooltip>
                          {canUpdate && (
                            <Tooltip content="Editar">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openEdit(item)}
                                aria-label={`Editar ${item.title}`}
                              >
                                <PencilIcon className="h-4 w-4" />
                              </Button>
                            </Tooltip>
                          )}
                          {canDelete && (
                            <Tooltip content="Excluir">
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 hover:text-red-700"
                                onClick={() => void handleDelete(item)}
                                aria-label={`Excluir ${item.title}`}
                              >
                                <TrashIcon className="h-4 w-4" />
                              </Button>
                            </Tooltip>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <Text size="sm" color="muted">
                Página {currentPage} de {totalPages}
              </Text>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={currentPage <= 1 || loading}
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                >
                  Anterior
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={currentPage >= totalPages || loading}
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                >
                  Próxima
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {showFormModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                {editing ? 'Editar fluxo' : 'Novo fluxo'}
              </Heading>
              <Button size="sm" variant="outline" onClick={() => setShowFormModal(false)}>
                <XMarkIcon className="h-4 w-4" />
              </Button>
            </div>
            <div className="p-6 space-y-4 overflow-y-auto flex-1">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Título *
                </label>
                <Input
                  value={formData.title}
                  onChange={e => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  disabled={saving}
                />
                {formErrors.title && (
                  <Text size="sm" className="text-red-600 mt-1">
                    {formErrors.title}
                  </Text>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Descrição
                </label>
                <textarea
                  value={formData.description}
                  onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  disabled={saving}
                  rows={3}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/30"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  JSON *
                </label>
                <div
                  className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4 text-center cursor-pointer hover:border-primary-400 dark:hover:border-primary-500 transition-colors mb-2"
                  onClick={() => !saving && jsonFileInputRef.current?.click()}
                >
                  <input
                    ref={jsonFileInputRef}
                    type="file"
                    accept=".json,application/json"
                    className="hidden"
                    onChange={e => void handleJsonFileSelect(e)}
                    disabled={saving}
                  />
                  <ArrowUpTrayIcon className="mx-auto h-7 w-7 text-gray-400 dark:text-gray-500 mb-1" />
                  <Text size="sm" color="muted">
                    Clique para selecionar um arquivo JSON
                  </Text>
                </div>
                <textarea
                  value={formData.flow_json}
                  onChange={e => setFormData(prev => ({ ...prev, flow_json: e.target.value }))}
                  disabled={saving}
                  rows={14}
                  spellCheck={false}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm font-mono text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/30"
                />
                {formErrors.flow_json && (
                  <Text size="sm" className="text-red-600 mt-1">
                    {formErrors.flow_json}
                  </Text>
                )}
                {!formErrors.flow_json && formData.flow_json.trim() && !isValidJson(formData.flow_json) && (
                  <Text size="sm" className="text-amber-600 dark:text-amber-400 mt-1">
                    JSON inválido — corrija para habilitar o salvamento
                  </Text>
                )}
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowFormModal(false)} disabled={saving}>
                Cancelar
              </Button>
              <Button
                variant="solid"
                size="sm"
                onClick={() => void handleSave()}
                disabled={saving || !canSaveForm}
              >
                {saving ? 'Salvando...' : 'Salvar'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {showViewModal && viewing && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <div>
                <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                  {viewing.title}
                </Heading>
                <Text size="sm" color="muted" className="mt-1">
                  Versão atual: v{viewing.current_version || 0} · Atualizado em{' '}
                  {formatDateTime(viewing.updated_at)}
                </Text>
              </div>
              <Button size="sm" variant="outline" onClick={() => setShowViewModal(false)}>
                <XMarkIcon className="h-4 w-4" />
              </Button>
            </div>
            <div className="p-6 space-y-4 overflow-y-auto flex-1">
              {viewing.description && (
                <div>
                  <Text size="sm" weight="semibold" className="mb-1">
                    Descrição
                  </Text>
                  <Text size="sm" className="whitespace-pre-wrap text-gray-700 dark:text-gray-300">
                    {viewing.description}
                  </Text>
                </div>
              )}
              <div>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <Text size="sm" weight="semibold">
                    JSON
                  </Text>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => downloadJsonFile(viewing.flow_json, viewing.title)}
                  >
                    <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                    Baixar JSON
                  </Button>
                </div>
                <pre className="whitespace-pre-wrap text-sm font-mono bg-gray-50 dark:bg-gray-900 p-4 rounded-lg overflow-x-auto text-gray-800 dark:text-gray-200">
                  {formatFlowJson(viewing.flow_json)}
                </pre>
              </div>
            </div>
          </div>
        </div>
      )}

      {showVersionsModal && versionsItem && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                Histórico: {versionsItem.title}
              </Heading>
              <div className="flex items-center gap-2">
                {versions.length > 1 && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowVersionDiffModal(true)}
                    className="inline-flex items-center gap-1"
                  >
                    <AdjustmentsHorizontalIcon className="h-4 w-4" />
                    Comparar
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => setShowVersionsModal(false)}>
                  <XMarkIcon className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              {versionsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <LoadingSpinner />
                </div>
              ) : versions.length === 0 ? (
                <Text color="muted">Nenhuma versão encontrada.</Text>
              ) : (
                <div className="space-y-3">
                  {versions.map(version => (
                    <div
                      key={version.id}
                      className="rounded-lg border border-gray-200 dark:border-gray-700 p-4"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <Text weight="semibold">v{version.version_number}</Text>
                          <Text size="sm" color="muted">
                            {formatDateTime(version.created_at)} · {version.user_name || 'Usuário'}
                          </Text>
                          <Text size="sm" className="mt-1 text-gray-600 dark:text-gray-300">
                            {version.change_summary || 'Sem descrição das mudanças'}
                          </Text>
                        </div>
                        <div className="flex gap-2 shrink-0">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setViewingVersion(version)
                              setShowVersionViewModal(true)
                            }}
                          >
                            Visualizar
                          </Button>
                          {canUpdate && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-green-600 hover:text-green-700"
                              onClick={() => void handleRestoreVersion(version.version_number)}
                            >
                              Restaurar
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showVersionViewModal && viewingVersion && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <div>
                <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                  Versão {viewingVersion.version_number}: {viewingVersion.title}
                </Heading>
                <Text size="sm" color="muted" className="mt-1">
                  {formatDateTime(viewingVersion.created_at)}
                </Text>
              </div>
              <Button size="sm" variant="outline" onClick={() => setShowVersionViewModal(false)}>
                <XMarkIcon className="h-4 w-4" />
              </Button>
            </div>
            <div className="p-6 space-y-4 overflow-y-auto flex-1">
              {viewingVersion.description && (
                <div>
                  <Text size="sm" weight="semibold" className="mb-1">
                    Descrição
                  </Text>
                  <Text size="sm" className="whitespace-pre-wrap">
                    {viewingVersion.description}
                  </Text>
                </div>
              )}
              <div>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <Text size="sm" weight="semibold">
                    JSON
                  </Text>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      downloadJsonFile(
                        viewingVersion.flow_json,
                        `${viewingVersion.title}-v${viewingVersion.version_number}`
                      )
                    }
                  >
                    <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                    Baixar JSON
                  </Button>
                </div>
                <pre className="whitespace-pre-wrap text-sm font-mono bg-gray-50 dark:bg-gray-900 p-4 rounded-lg overflow-x-auto">
                  {formatFlowJson(viewingVersion.flow_json)}
                </pre>
              </div>
            </div>
          </div>
        </div>
      )}

      {showVersionDiffModal && versions.length > 0 && (
        <VersionDiff
          versions={toDiffVersions(versions)}
          onClose={() => setShowVersionDiffModal(false)}
        />
      )}
    </div>
  )
}

export default FluxoN8nListaTab
